import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CardNode from './CardNode';
import '../styles/workflow.css';

const NODE_WIDTH = 141;
const NODE_GAP_X = 51;
const NODE_GAP_Y = 36;
const CANVAS_PADDING = 40;

const getNodeHeight = (node) => {
    const inputCount = Math.max(Array.isArray(node.from) ? node.from.length : 0, 1);
    return 120 + (inputCount - 1) * 28;
};

const computeLayout = (nodes) => {
    if (!nodes.length) {
        return { positions: {}, canvasWidth: 800, canvasHeight: 320 };
    }

    const byId = Object.fromEntries(nodes.map((node) => [node.id, node]));
    const levels = {};

    const getLevel = (id, visiting = new Set()) => {
        if (levels[id] !== undefined) {
            return levels[id];
        }
        if (visiting.has(id)) {
            levels[id] = 0;
            return 0;
        }

        visiting.add(id);
        const node = byId[id];
        const fromIds = Array.isArray(node?.from) ? node.from.filter((fromId) => byId[fromId]) : [];

        if (!fromIds.length) {
            levels[id] = 0;
            return 0;
        }

        const level = Math.max(...fromIds.map((fromId) => getLevel(fromId, visiting))) + 1;
        levels[id] = level;
        return level;
    };

    nodes.forEach((node) => getLevel(node.id));

    const columns = {};
    nodes.forEach((node) => {
        const level = levels[node.id] ?? 0;
        if (!columns[level]) {
            columns[level] = [];
        }
        columns[level].push(node);
    });

    const positions = {};
    let maxX = CANVAS_PADDING;
    let maxY = CANVAS_PADDING + 120;

    Object.keys(columns)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((levelKey) => {
            const level = Number(levelKey);
            const columnNodes = columns[level].sort((a, b) => a.id - b.id);
            let columnY = CANVAS_PADDING;

            columnNodes.forEach((node) => {
                const x = CANVAS_PADDING + level * (NODE_WIDTH + NODE_GAP_X);
                positions[node.id] = { x, y: columnY };
                columnY += getNodeHeight(node) + NODE_GAP_Y;
                maxX = Math.max(maxX, x + NODE_WIDTH + CANVAS_PADDING);
                maxY = Math.max(maxY, columnY);
            });
        });

    return {
        positions,
        canvasWidth: Math.max(maxX, 800),
        canvasHeight: Math.max(maxY, 320),
    };
};

const buildConnectionPath = (x1, y1, x2, y2) => {
    const controlOffset = Math.max(Math.abs(x2 - x1) * 0.5, 40);
    return `M ${x1} ${y1} C ${x1 + controlOffset} ${y1}, ${x2 - controlOffset} ${y2}, ${x2} ${y2}`;
};

const NodesWorkflow = ({ nodes, running, onSettingsClick }) => {
    const canvasRef = useRef(null);
    const [lines, setLines] = useState([]);

    const layout = useMemo(() => computeLayout(nodes), [nodes]);

    const updateLines = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !nodes.length) {
            setLines([]);
            return;
        }

        const canvasRect = canvas.getBoundingClientRect();
        const nextLines = [];

        nodes.forEach((node) => {
            const fromIds = Array.isArray(node.from) ? node.from : [];
            fromIds.forEach((fromId, inputIndex) => {
                const outputPort = canvas.querySelector(`[data-port-out="${fromId}"]`);
                const inputPort = canvas.querySelector(`[data-port-in="${node.id}-${inputIndex}"]`);

                if (!outputPort || !inputPort) {
                    return;
                }

                const outputRect = outputPort.getBoundingClientRect();
                const inputRect = inputPort.getBoundingClientRect();

                nextLines.push({
                    key: `${fromId}-${node.id}-${inputIndex}`,
                    path: buildConnectionPath(
                        outputRect.right - canvasRect.left,
                        outputRect.top + outputRect.height / 2 - canvasRect.top,
                        inputRect.left - canvasRect.left,
                        inputRect.top + inputRect.height / 2 - canvasRect.top
                    ),
                });
            });
        });

        setLines(nextLines);
    }, [nodes]);

    useEffect(() => {
        updateLines();

        const canvas = canvasRef.current;
        if (!canvas) {
            return undefined;
        }

        const observer = new ResizeObserver(() => updateLines());
        observer.observe(canvas);

        window.addEventListener('resize', updateLines);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateLines);
        };
    }, [updateLines, layout]);

    if (!nodes.length) {
        return <div className="workflow-empty">Nenhuma etapa definida. Clique em Adicionar para criar uma etapa.</div>;
    }

    return (
        <div className="workflow-canvas-wrapper">
            <div
                ref={canvasRef}
                className="workflow-canvas"
                style={{ width: layout.canvasWidth, height: layout.canvasHeight }}
            >
                <svg className="workflow-lines" width={layout.canvasWidth} height={layout.canvasHeight}>
                    {lines.map((line) => (
                        <path
                            key={line.key}
                            d={line.path}
                            fill="none"
                            stroke="#1677ff"
                            strokeWidth="2"
                        />
                    ))}
                </svg>

                {nodes.map((node) => {
                    const position = layout.positions[node.id] || { x: CANVAS_PADDING, y: CANVAS_PADDING };
                    return (
                        <div
                            key={node.id}
                            className="workflow-node-wrapper"
                            style={{ left: position.x, top: position.y, width: NODE_WIDTH }}
                        >
                            <CardNode
                                node={node}
                                nodes={nodes}
                                running={running}
                                onSettingsClick={onSettingsClick}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default NodesWorkflow;
